/**
 * HitterIntelBriefing - Premium Synthesized Pitch Tipping Intelligence
 *
 * Beautiful, simple, easy to read display of Gemini 2.5 Pro synthesized
 * hitter intelligence from THE WIRE analysis.
 */

import React, { useState } from 'react';
import {
  Zap,
  Eye,
  Target,
  AlertTriangle,
  Shield,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Users,
  Clock,
  Crosshair
} from 'lucide-react';

// =============================================================================
// THREAT LEVEL BADGE
// =============================================================================

function ThreatLevelBadge({ level }) {
  const config = {
    CRITICAL: {
      bg: 'bg-gradient-to-r from-red-600 to-red-500',
      border: 'border-red-400/50',
      text: 'text-white',
      glow: 'shadow-red-500/30',
      icon: AlertTriangle,
      pulse: true
    },
    HIGH: {
      bg: 'bg-gradient-to-r from-orange-600 to-orange-500',
      border: 'border-orange-400/50',
      text: 'text-white',
      glow: 'shadow-orange-500/30',
      icon: Zap,
      pulse: false
    },
    MEDIUM: {
      bg: 'bg-gradient-to-r from-yellow-600 to-yellow-500',
      border: 'border-yellow-400/50',
      text: 'text-black',
      glow: 'shadow-yellow-500/20',
      icon: Eye,
      pulse: false
    },
    LOW: {
      bg: 'bg-gradient-to-r from-green-600 to-green-500',
      border: 'border-green-400/50',
      text: 'text-white',
      glow: 'shadow-green-500/20',
      icon: Shield,
      pulse: false
    }
  };

  const c = config[level] || config.MEDIUM;
  const Icon = c.icon;

  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${c.bg} ${c.text} border ${c.border} shadow-lg ${c.glow} font-bold text-sm uppercase tracking-wider`}>
      <Icon className={`w-4 h-4 ${c.pulse ? 'animate-pulse' : ''}`} />
      {level}
      {c.pulse && (
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
        </span>
      )}
    </div>
  );
}

// =============================================================================
// TIPPING RATE RING
// =============================================================================

function TippingRateRing({ rate, size = 'lg' }) {
  const percentage = rate * 100;
  const sizes = {
    sm: { w: 64, r: 26, stroke: 4 },
    md: { w: 80, r: 34, stroke: 5 },
    lg: { w: 120, r: 50, stroke: 6 }
  };
  const s = sizes[size];
  const circumference = 2 * Math.PI * s.r;
  const offset = circumference - (percentage / 100) * circumference;

  const color = percentage >= 70 ? '#ef4444' : percentage >= 50 ? '#f97316' : percentage >= 30 ? '#eab308' : '#22c55e';

  return (
    <div className="relative" style={{ width: s.w, height: s.w }}>
      <svg className="w-full h-full transform -rotate-90">
        <circle
          cx={s.w / 2}
          cy={s.w / 2}
          r={s.r}
          stroke="#27272a"
          strokeWidth={s.stroke}
          fill="none"
        />
        <circle
          cx={s.w / 2}
          cy={s.w / 2}
          r={s.r}
          stroke={color}
          strokeWidth={s.stroke}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold font-mono" style={{ color }}>
          {percentage.toFixed(0)}%
        </span>
        <span className="text-[10px] text-zinc-500 uppercase">Tipping</span>
      </div>
    </div>
  );
}

// =============================================================================
// PRIMARY TELL CARD
// =============================================================================

function PrimaryTellCard({ tell }) {
  if (!tell) return null;

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500/10 via-zinc-900 to-zinc-900 border border-orange-500/30">
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/4 w-1/2 h-32 bg-orange-500/20 blur-3xl" />

      <div className="relative p-6">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-500/30">
            <Crosshair className="w-7 h-7 text-white" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-bold text-orange-400 uppercase tracking-wider">Primary Tell</span>
              <span className="px-2 py-0.5 text-[10px] font-bold bg-orange-500/20 text-orange-300 rounded-full uppercase">
                {tell.pitchRevealed}
              </span>
            </div>

            <h3 className="text-xl font-bold text-white leading-tight mb-3">
              {tell.whatToLookFor}
            </h3>

            <div className="flex flex-wrap items-center gap-3 text-sm">
              <div className="flex items-center gap-1.5 text-zinc-400">
                <Clock className="w-4 h-4" />
                <span>{tell.whenItHappens}</span>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-zinc-800 border border-zinc-700">
                <span className="text-zinc-400">Confidence:</span>
                <span className="font-bold text-orange-400">{tell.confidence}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// SECONDARY TELLS LIST
// =============================================================================

function SecondaryTellsList({ tells }) {
  const [expanded, setExpanded] = useState(false);

  if (!tells || tells.length === 0) return null;

  const displayTells = expanded ? tells : tells.slice(0, 2);

  return (
    <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 overflow-hidden">
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <h4 className="font-bold text-white flex items-center gap-2">
            <Eye className="w-4 h-4 text-yellow-400" />
            Secondary Tells
          </h4>
          <span className="text-xs text-zinc-500 font-mono">{tells.length} identified</span>
        </div>
      </div>

      <div className="divide-y divide-zinc-800/50">
        {displayTells.map((tell, idx) => (
          <div key={idx} className="p-4 hover:bg-zinc-800/30 transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center flex-shrink-0 border border-yellow-500/20">
                <span className="text-xs font-bold text-yellow-400">{idx + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white leading-relaxed">{tell.whatToLookFor}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="px-2 py-0.5 text-[10px] font-mono bg-zinc-800 text-zinc-300 rounded">
                    {tell.pitchRevealed}
                  </span>
                  {tell.confidence && (
                    <span className="text-[10px] text-zinc-500">
                      {tell.confidence} confidence
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {tells.length > 2 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full p-3 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors flex items-center justify-center gap-1"
        >
          {expanded ? (
            <>Show Less <ChevronUp className="w-4 h-4" /></>
          ) : (
            <>Show {tells.length - 2} More <ChevronDown className="w-4 h-4" /></>
          )}
        </button>
      )}
    </div>
  );
}

// =============================================================================
// AT-BAT STRATEGY CARD
// =============================================================================

function AtBatStrategyCard({ strategy }) {
  const [activeTab, setActiveTab] = useState('firstPitch');

  if (!strategy) return null;

  const tabs = [
    { key: 'firstPitch', label: 'First Pitch', icon: '1st' },
    { key: 'aheadInCount', label: 'Ahead', icon: '+' },
    { key: 'behindInCount', label: 'Behind', icon: '-' },
    { key: 'twoStrikes', label: 'Two Strikes', icon: '2K' }
  ];

  return (
    <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800 bg-gradient-to-r from-blue-500/5 to-transparent">
        <h4 className="font-bold text-white flex items-center gap-2">
          <Target className="w-4 h-4 text-blue-400" />
          At-Bat Strategy
        </h4>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-zinc-800 bg-zinc-900/50">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 px-3 py-3 text-xs font-medium transition-all border-b-2 ${
              activeTab === tab.key
                ? 'border-blue-500 text-blue-400 bg-blue-500/10'
                : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <span className="block text-lg font-bold font-mono mb-0.5">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-5">
        <p className="text-sm text-zinc-300 leading-relaxed">
          {strategy[activeTab]}
        </p>

        {/* Stretch vs Windup */}
        {strategy.stretchVsWindup && (
          <div className="mt-4 pt-4 border-t border-zinc-800">
            <div className="flex items-start gap-2">
              <Users className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-[10px] font-bold text-purple-400 uppercase tracking-wider">Stretch vs Windup</span>
                <p className="text-xs text-zinc-400 mt-1">{strategy.stretchVsWindup}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// EXPLOITATION MATRIX
// =============================================================================

function ExploitationMatrix({ matrix }) {
  if (!matrix || matrix.length === 0) return null;

  const getReliabilityColor = (reliability) => {
    const r = reliability?.toUpperCase() || '';
    if (r.includes('HIGH')) return 'text-red-400 bg-red-500/10 border-red-500/30';
    if (r.includes('MODERATE') || r.includes('MEDIUM')) return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
    return 'text-green-400 bg-green-500/10 border-green-500/30';
  };

  return (
    <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 overflow-hidden">
      <div className="p-4 border-b border-zinc-800">
        <h4 className="font-bold text-white flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-400" />
          Exploitation Matrix
        </h4>
        <p className="text-xs text-zinc-500 mt-1">Pitch-by-pitch tipping reliability</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800 bg-zinc-900/50">
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Pitch</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Category</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-zinc-500 uppercase tracking-wider">Freq</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-zinc-500 uppercase tracking-wider">Reliability</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider hidden md:table-cell">Look For</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {matrix.slice(0, 6).map((row, idx) => (
              <tr key={idx} className="hover:bg-zinc-800/30 transition-colors">
                <td className="px-4 py-3">
                  <span className="font-mono font-bold text-white">{row.pitchRevealed}</span>
                </td>
                <td className="px-4 py-3 text-zinc-400">
                  {row.categoryLabel || row.category}
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="font-mono text-zinc-300">
                    {((row.frequency || 0) * 100).toFixed(0)}%
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 text-[10px] font-medium rounded-full border ${getReliabilityColor(row.reliability)}`}>
                    {row.reliability || row.confidenceLevel}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-zinc-500 hidden md:table-cell max-w-xs truncate">
                  {row.description}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// SITUATIONAL INTEL CARD
// =============================================================================

function SituationalIntelCard({ situationalKeys }) {
  if (!situationalKeys) return null;

  return (
    <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-4">
      <h4 className="font-bold text-white flex items-center gap-2 mb-4">
        <Users className="w-4 h-4 text-purple-400" />
        Situational Keys
      </h4>

      <div className="space-y-4">
        {situationalKeys.stretch && (
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center flex-shrink-0 border border-purple-500/20">
              <span className="text-xs font-bold text-purple-400">SET</span>
            </div>
            <div>
              <span className="text-xs font-bold text-purple-400 uppercase">From the Stretch</span>
              <p className="text-sm text-zinc-400 mt-1">{situationalKeys.stretch}</p>
            </div>
          </div>
        )}

        {situationalKeys.pressure && (
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0 border border-red-500/20">
              <AlertTriangle className="w-4 h-4 text-red-400" />
            </div>
            <div>
              <span className="text-xs font-bold text-red-400 uppercase">High Pressure</span>
              <p className="text-sm text-zinc-400 mt-1">{situationalKeys.pressure}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function HitterIntelBriefing({ pitcherData }) {
  const {
    threatAssessment,
    primaryTell,
    secondaryTells,
    atBatStrategy,
    exploitationMatrix,
    situationalKeys
  } = pitcherData || {};

  if (!threatAssessment) {
    return null; // No synthesis data available
  }

  return (
    <div className="space-y-6">
      {/* Hero Section - Threat Assessment */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-zinc-800 via-zinc-900 to-zinc-900 border border-zinc-700">
        {/* Gradient background based on threat level */}
        <div className={`absolute inset-0 opacity-20 ${
          threatAssessment.level === 'CRITICAL' ? 'bg-gradient-to-br from-red-600 via-transparent to-transparent' :
          threatAssessment.level === 'HIGH' ? 'bg-gradient-to-br from-orange-600 via-transparent to-transparent' :
          'bg-gradient-to-br from-yellow-600 via-transparent to-transparent'
        }`} />

        <div className="relative p-6 lg:p-8">
          <div className="flex flex-col lg:flex-row lg:items-start gap-6">
            {/* Left - Badge and Stats */}
            <div className="flex flex-col items-center lg:items-start gap-4">
              <ThreatLevelBadge level={threatAssessment.level} />
              <TippingRateRing rate={threatAssessment.tippingRate} size="lg" />
              <div className="text-center lg:text-left">
                <p className="text-xs text-zinc-500">Based on</p>
                <p className="text-lg font-bold text-white font-mono">{threatAssessment.totalAnalyzed}</p>
                <p className="text-xs text-zinc-500">pitches analyzed</p>
              </div>
            </div>

            {/* Right - Headline and Quick Read */}
            <div className="flex-1 lg:border-l lg:border-zinc-700 lg:pl-8">
              <h2 className="text-2xl lg:text-3xl font-bold text-white leading-tight mb-4">
                {threatAssessment.headline}
              </h2>

              <p className="text-zinc-300 leading-relaxed mb-6">
                {threatAssessment.quickRead}
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <div className="px-4 py-2 rounded-xl bg-zinc-800 border border-zinc-700">
                  <span className="text-xs text-zinc-500">Most Exploitable</span>
                  <p className="text-lg font-bold font-mono text-orange-400">
                    {threatAssessment.mostExploitablePitch}
                  </p>
                </div>
                <div className="px-4 py-2 rounded-xl bg-zinc-800 border border-zinc-700">
                  <span className="text-xs text-zinc-500">Tells Detected</span>
                  <p className="text-lg font-bold font-mono text-white">
                    {threatAssessment.tippingDetected}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Primary Tell */}
      <PrimaryTellCard tell={primaryTell} />

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          <SecondaryTellsList tells={secondaryTells} />
          <SituationalIntelCard situationalKeys={situationalKeys} />
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <AtBatStrategyCard strategy={atBatStrategy} />
        </div>
      </div>

      {/* Full Width Exploitation Matrix */}
      <ExploitationMatrix matrix={exploitationMatrix} />
    </div>
  );
}
